// Custom-font resolution for the collector iframe.
//
// Flow:
//   1. fetchConsentProfile receives `consentUI.cssStyleProperties`. Two kinds
//      of entries participate in font resolution:
//        - `FONT_NAME`         → the client-configured family name (used as-is)
//        - `WEIGHT_<num>`      → CDN URL for that weight (e.g. WEIGHT_400)
//   2. deriveCustomFontFromCssProps() maps that into the cache record shape
//      { consentProfileId, fontName, weights: [{ weight, url }] }.
//   3. The record is written to localStorage and pushed to state.customFont.
//   4. On the next SDK open, store.js hydrates state.customFont from
//      localStorage *before* the first render, so the @font-face rule and
//      CustomProvider's FontFace download both start at mount instead of
//      waiting for the API — that's what removes the visible Lato→custom swap.
//   5. AppProvider reads it through useCustomFont() and hands the library's
//      CustomProvider a themeConfig with:
//        - fontFamily : the client's family name  (--font-family-base)
//        - fontFaces  : descriptors the library registers via the FontFace API
//
// Note: we intentionally do NOT ship a fallback stack (Lato, Helvetica, etc.).
// Lato is the component library's built-in default — CustomProvider applies it
// automatically when themeConfig.fontFamily is undefined, and its base CSS
// keeps rendering Lato when a custom face fails to download. Passing our own
// stack here would just duplicate that logic and hardcode the fallback name.

import { StorageKeys } from '@constants/storage';
import {
  collectFontName, collectFontWeights, isSafeFontFamilyName, toSafeFontFamilyName,
} from '@utils/font-face-rule';

const EMPTY_FONT_FACES = [];

// consent-center: consentUI.cssStyleProperties
const getPropertyName = (entry) => entry?.propertyName;
const getPropertyValue = (entry) => entry?.propertyValue;
// privacy-center: configDetails.PRIVACY_CENTER
const getConfigKey = (entry) => entry?.configKey;
const getConfigValue = (entry) => entry?.content;

// fontName is whatever the API sent under FONT_NAME — no synthetic wrapping —
// so the DOM inspector shows the real client-configured name (e.g. "TCCC
// Unity") and the value stays consistent across tenants. Returns null when the
// tenant has no usable font configured, so the downstream stack (and the cache)
// can defer to the component library's default.
const deriveFont = (entries, getKey, getValue) => {
  const fontName = collectFontName(entries, getKey, getValue);
  const weights = collectFontWeights(entries, getKey, getValue);
  return fontName && weights.length > 0 ? { fontName, weights } : null;
};

// Shape consentUI.cssStyleProperties into the cache record, tagged with the
// consent profile it belongs to.
export const deriveCustomFontFromCssProps = (cssStyleProperties, consentProfileId) => {
  const font = deriveFont(cssStyleProperties, getPropertyName, getPropertyValue);
  return font ? { consentProfileId: consentProfileId || null, ...font } : null;
};

// v2 details: consentUI.font = { id, name, weights: [{ value: 'WEIGHT_600', url }] }
// (consentUI.fontFamily is just that font's id). Same record shape as v1, so the
// cache, the @font-face rule and CustomProvider all work unchanged.
const getFontWeightKey = (entry) => entry?.value;
const getFontWeightUrl = (entry) => entry?.url;

export const deriveCustomFontFromConsentFont = (font, consentProfileId) => {
  const fontName = toSafeFontFamilyName(font?.name);
  const weights = collectFontWeights(font?.weights, getFontWeightKey, getFontWeightUrl);
  return fontName && weights.length > 0
    ? { consentProfileId: consentProfileId || null, fontName, weights }
    : null;
};

// Same for privacy-center, whose config arrives under configDetails
// .PRIVACY_CENTER and is scoped to the authenticated principal rather than to a
// single consent profile.
export const deriveCustomFontFromConfigDetails = (privacyCenterConfig, principalId) => {
  const font = deriveFont(privacyCenterConfig, getConfigKey, getConfigValue);
  return font ? { principalId: principalId || null, ...font } : null;
};

// Returns the family name string to pass through themeConfig.fontFamily, or
// null when no custom font is configured — CustomProvider then falls back to
// its bundled Lato default all by itself.
export const resolveFontFamily = (customFont) => {
  const fontName = customFont?.fontName;
  return isSafeFontFamilyName(fontName) ? `"${fontName.trim()}"` : null;
};

// Library-shaped font-face descriptors. Empty array when there's nothing to
// download, so CustomProvider can no-op.
export const buildFontFaces = (customFont) => {
  const fontName = customFont?.fontName;
  const weights = customFont?.weights;
  let fontFaces = EMPTY_FONT_FACES;
  if (isSafeFontFamilyName(fontName) && Array.isArray(weights) && weights.length > 0) {
    fontFaces = weights.map(({ url, weight }) => ({
      family: fontName.trim(), src: url, weight, style: 'normal',
    }));
  }
  return fontFaces;
};

// localStorage caches. Shapes differ because the two centers are scoped
// differently:
//
//   consent_custom_font        — the collector serves many consent profiles
//                                from one origin, so records are keyed to keep
//                                them from stomping on each other:
//     {
//       consentProfileId: '<id>',
//       profiles: { '<id>': { consentProfileId, fontName, weights } },
//     }
//
//   privacy_center_custom_font — privacy-center's whole config arrives on one
//                                PRIVACY_CENTER array for the one
//                                authenticated principal, so a single flat
//                                record is all it needs:
//     { principalId: '<id>', fontName: '…', weights: [{ weight, url }] }
//
// The stored id is what makes a cold boot possible: both centers open via a
// signed URL that exposes no identifier, so before the API answers this is the
// only way to know whose font to paint with.

// Only the collector uses the consent-center shape; every other center type
// (PVC / PFC / RQC) renders through privacy-center.
const CONSENT_URL_TYPE = 'consentCollectUrl';

// Deliberately does NOT touch window.localStorage: merely reading that property
// throws a SecurityError in a sandboxed iframe or when the browser has storage
// disabled. store.js calls into this module at module-evaluation time, so a
// throw here would take the whole app down before anything renders. Every actual
// localStorage access below sits inside a try instead.
const isBrowser = () => typeof window !== 'undefined';

const readStored = (storageKey) => {
  let stored = null;
  if (isBrowser()) {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey));
      if (parsed && typeof parsed === 'object') stored = parsed;
    } catch {
      // Storage blocked, absent, or corrupt entry — treat as a cache miss.
    }
  }
  return stored;
};

// value === null clears the entry.
const writeStored = (storageKey, value) => {
  if (!isBrowser()) return;
  try {
    if (value) {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch {
    // Ignore storage-blocked / quota / private-mode errors — the cache is a
    // nice-to-have.
  }
};

// Belt-and-suspenders: a corrupt localStorage entry must not reach the CSS or
// crash the modal on hydrate.
const isValidRecord = (record) => isSafeFontFamilyName(record?.fontName)
  && Array.isArray(record?.weights)
  && record.weights.length > 0;

// Pass a consentProfileId once it's known (post-API). Omit it at boot to get
// the last profile opened on this origin — the common single-profile case.
const readCachedCustomFont = (consentProfileId) => {
  const cache = readStored(StorageKeys.CONSENT_CUSTOM_FONT) || {};
  const id = consentProfileId || cache.consentProfileId;
  const record = id ? cache.profiles?.[id] : null;
  return isValidRecord(record) ? record : null;
};

// Pass customFont === null when the tenant has no font configured, so a font
// that was removed upstream stops being painted on the next open.
export const writeCachedCustomFont = (consentProfileId, customFont) => {
  if (!consentProfileId || !isBrowser()) return;
  const cache = readStored(StorageKeys.CONSENT_CUSTOM_FONT) || {};
  const profiles = { ...cache.profiles };
  if (customFont) {
    profiles[consentProfileId] = { ...customFont, consentProfileId };
  } else {
    delete profiles[consentProfileId];
  }
  writeStored(StorageKeys.CONSENT_CUSTOM_FONT, { consentProfileId, profiles });
};

// Single record, so a principalId that doesn't match what's stored is a miss
// rather than a lookup — it means the entry belongs to someone else.
const readCachedPrivacyCenterFont = (principalId) => {
  const record = readStored(StorageKeys.PRIVACY_CENTER_CUSTOM_FONT);
  const isSamePrincipal = !principalId || record?.principalId === principalId;
  return isSamePrincipal && isValidRecord(record) ? record : null;
};

export const writeCachedPrivacyCenterFont = (principalId, customFont) => {
  if (!principalId || !isBrowser()) return;
  const record = customFont ? { ...customFont, principalId } : null;
  writeStored(StorageKeys.PRIVACY_CENTER_CUSTOM_FONT, record);
};

// Which cache to hydrate the store from at boot. The router's loader derives
// the center type from the same query param, but it runs after this module is
// evaluated, so read it straight off the URL here.
const getUrlType = () => {
  let type = null;
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      type = params.get('type') || params.get('urlType');
    } catch {
      type = null;
    }
  }
  return type;
};

// Only the SDK/center flows (/collect, /privacy-center) carry a `type` URL
// param; the EUP dashboard, login and profile selection never do.
export const hasCenterUrlType = () => !!getUrlType();

// EUP renders in the product's own theme, so with no center type in the URL
// nothing is hydrated — a privacy-center font cached on this origin must not
// leak into the dashboard.
export const readCachedFontForCurrentCenter = () => {
  let record = null;
  const type = getUrlType();
  if (type === CONSENT_URL_TYPE) {
    record = readCachedCustomFont();
  } else if (type) {
    record = readCachedPrivacyCenterFont();
  }
  return record;
};
